import { z } from 'zod'
import {
  Carrier,
  ExchangeColor,
  ExchangeOption,
  ProductSizes,
  ReturnColor,
  ReturnField,
  ReturnInfo,
  ReturnReason,
  ReturnsOrder
} from '../types'

const ZReasonValue = z.object({
  label: z.string(),
  value: z.string(),
  id: z.coerce.string()
})

const ZColor = z.object({
  primary: z.string(),
  secondary: z.string().nullable(),
  image: z.string().nullable()
})

const ZReasonField: z.ZodType<ReturnField> = z.object({
  label: z.string(),
  type: z.string(),
  values: z.array(ZReasonValue)
})

export const ZReturnReason: z.ZodType<ReturnReason> = z.object({
  id: z.number(),
  title: z.string(),
  fields: z.array(ZReasonField).optional()
})

export const ZSizes: z.ZodType<Partial<ProductSizes>> = z.object({
  waist: z.string().optional(),
  build: z.string().optional(),
  leg: z.string().optional(),
  size: z.string().optional(),
  cut: z.string().optional(),
  length: z.string().optional(),
  shortsleg: z.string().optional(),
  chest_size: z.string().optional(),
  taper: z.string().optional(),
  amount: z.string().optional()
})

export const ZExchangeOption: z.ZodType<ExchangeOption> = z.object({
  name: z.string(),
  values: z.array(z.string()),
  type: z.string()
})

export const ZExchangeColor: z.ZodType<ExchangeColor> = z.object({
  title: z.string(),
  product_id: z.number(),
  image: z.object({ src: z.string() }),
  color: ZColor.optional()
})

export const ZReturnBlockColor: z.ZodType<ReturnColor> = z.object({
  title: z.string(),
  product_id: z.number(),
  image: z.object({ src: z.string() }),
  sku_16: z.string().optional()
})

export const ZProductBase = z.object({
  product_id: z.number(),
  id: z.string(),
  variant_id: z.number(),
  product_type: z.string(),
  image: z.string().default(''),
  sizes: ZSizes,
  paid_price: z.number(),
  full_price: z.string(),
  isMTO: z.boolean(),
  title: z.string(),
  color: z.string().optional(),
  discount_allocations: z.number().optional().default(0),
  sku: z.string(),
  isReturnable: z.boolean(),
  isExchangeable: z.boolean(),
  return_reasons: z.array(ZReturnReason),
  exchange_options: z.array(ZExchangeOption).nonempty({
    message: 'exchange_options must contain at least one option'
  }),
  exchange_colors: z.array(ZExchangeColor),
  sale_item_id: z.string().optional()
})

export type ReturnsProductParsed = z.infer<typeof ZProductBase>

const ReturnMethodEnum = z.enum(['exchange', 'refund'])
export type ReturnMethod = z.infer<typeof ReturnMethodEnum>

export const ZReturnBlock = z.object({
  method: ReturnMethodEnum,
  reason: z.coerce.number(),
  sizes: ZSizes,
  return_date: z.string().datetime(),
  return_id: z.string(),
  color: ZReturnBlockColor.optional(),
  reason_detail: z.array(z.string())
})

export const ZProductWithReturn = ZProductBase.extend({
  return: ZReturnBlock,
  exchange_options: z.array(ZExchangeOption).optional()
})

export const ZReturnsEntry: z.ZodType<ReturnInfo> = z.object({
  products: z.array(ZProductWithReturn),
  return_date: z.string().datetime(),
  label_url: z.string()
})

// The keyed structure — allows {} just fine
export const ZReturnsProductsObject = z.record(z.string(), ZReturnsEntry)

const ZEmptyObject = z.object({})

export const ZReturnsUnion = z.union([
  z.record(z.string(), ZReturnsEntry),
  ZEmptyObject
])

export const ZReturnsCarrier: z.ZodType<Carrier> = z.object({
  id: z.coerce.number(),
  byline: z.string(),
  message: z.string(),
  modal_byline: z.string(),
  modal_message: z.string(),
  modal_title: z.string(),
  title: z.string()
})

export const ZReturnsCarriersValidator = z.array(ZReturnsCarrier)

export const ZReturnsOrder: z.ZodType<ReturnsOrder> = z.object({
  customer: z.object({
    id: z.number(),
    email: z.string().email(),
    address: z.object({
      title: z.string().nullable(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      street: z.string().nullable(),
      buildingOrCompanyName: z.string().nullable(),
      streetNumber: z.string().nullable(),
      streetNumberAddition: z.string().nullable(),
      suburb: z.string().nullable(),
      postalCode: z.string(),
      district: z.string().nullable(),
      city: z.string().nullable(),
      province: z.string().nullable(),
      language: z.string().nullable(),
      country: z.string().nullable(),
      phone1: z.string().nullable(),
      email: z.string().email()
    })
  }),
  order_number: z.string(),
  sale_order_id: z.string(),
  max_date: z.string(),
  currency: z.string(),
  expired: z.boolean(),
  postcode: z.string(),
  products: z.array(ZProductBase),
  carriers: z.array(ZReturnsCarrier),
  returns: ZReturnsUnion
})
